/**
 * What a project is, said where somebody is about to rely on it.
 *
 * THE MISUNDERSTANDING THIS PREVENTS
 *
 * A NimblyBase project is a BACKEND, not an app. One database, one user
 * pool, one set of keys, one cost total. Several clients of the same
 * product - a web app, an iOS app, a server - are meant to share one, and
 * that is the normal case rather than a compromise.
 *
 * Two UNRELATED products sharing one is the problem, and it is a quiet
 * problem. The tables coexist, the keys work, nothing errors. What happens
 * instead is that somebody who signed up to the first app can sign in to
 * the second with the same password, because there is one user table and
 * both apps trust it.
 *
 * The platform cannot tell these two cases apart: two apps using one key is
 * indistinguishable from one app on two machines. So the boundary is
 * explained rather than enforced, and it is explained BEFORE the point of
 * no return - at creation, and again before auth is switched on, which is
 * the moment the shared user pool starts to exist.
 *
 * WHY THE COPY LIVES HERE
 *
 * The CLI carries no runtime workspace dependency on purpose, so these
 * sentences cannot be one shared constant across every surface. What stops
 * them drifting is a test per surface asserting the claim, not a shared
 * string. See boundaries.test.ts.
 */

/** Said in the create dialog, before a project exists. */
export const AT_CREATION =
  'A project is one database, one user pool and one set of keys. '
  + 'Several clients of the same app — web, mobile, a server — should share one. '
  + 'Unrelated apps need their own, because separate code repositories and '
  + 'folder names do not create an authorization boundary.'

/**
 * Said before end-user auth is enabled.
 *
 * This is the consequence sentence. The page already explains whose users
 * these are; what it did not say is what sharing the project then means,
 * and that is the part somebody needs before they decide.
 */
export const BEFORE_AUTH =
  'Anyone who signs up here can sign in to any app using this project’s keys. '
  + 'That is what you want for several clients of one product, and not what '
  + 'you want for two different products.'
